'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  RotateCcw,
  Settings,
  LogOut,
  Library,
  Bookmark,
  BarChart3,
  Bell,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

const menuItems = [
  { icon: LayoutDashboard, label: '仪表盘', href: '/' },
  { icon: BookOpen, label: '图书管理', href: '/books' },
  { icon: Bookmark, label: '分类管理', href: '/categories' },
  { icon: RotateCcw, label: '借阅管理', href: '/borrow' },
  { icon: Users, label: '用户管理', href: '/users' },
  { icon: BarChart3, label: '统计报表', href: '/statistics' },
  { icon: Settings, label: '系统设置', href: '/settings' },
];

export function Sidebar() {
  const pathname = usePathname();

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-[280px] bg-[#020C1B] border-r border-[#1d3557]">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 h-16 border-b border-[#1d3557]">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#64FFDA] to-[#3B82F6] flex items-center justify-center">
          <Library className="w-6 h-6 text-[#0A192F]" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-[#E6F1FF] tracking-tight">智图管理系统</h1>
          <p className="text-xs text-[#8892B0]">Lumina Library OS</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = pathname === item.href || pathname?.startsWith(item.href + '/');
          
          return (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200",
                isActive
                  ? "bg-[#112240] text-[#64FFDA] border border-[#64FFDA]/20 glow-teal"
                  : "text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#112240]/50"
              )}
            >
              <Icon className={cn("w-5 h-5", isActive && "text-[#64FFDA]")} />
              {item.label}
              {item.label === '借阅管理' && (
                <Badge className="ml-auto bg-[#EF4444]/20 text-[#EF4444] border-0 text-xs">
                  28
                </Badge>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Notifications */}
      <div className="px-4 py-4 border-t border-[#1d3557]">
        <button className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#112240]/50 transition-all">
          <Bell className="w-5 h-5" />
          <span className="text-sm font-medium">消息通知</span>
          <Badge className="ml-auto bg-[#64FFDA]/20 text-[#64FFDA] border-0">
            5
          </Badge>
        </button>
      </div>

      {/* User Profile */}
      <div className="px-4 py-4 border-t border-[#1d3557]">
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#112240]/50">
          <Avatar className="w-10 h-10 border-2 border-[#64FFDA]/30">
            <AvatarImage src="https://images.unsplash.com/photo-1552158732-06dc1d835de0?w=200&q=80" alt="管理员" />
            <AvatarFallback className="bg-[#3B82F6] text-white">管理员</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#E6F1FF] truncate">系统管理员</p>
            <p className="text-xs text-[#8892B0]">admin@library.com</p>
          </div>
          <button className="text-[#8892B0] hover:text-[#EF4444] transition-colors">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
